// src/views/enseignant/ENHistoriquePointages.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useEnseignantService } from "../../services/enseignantService";
import { useEnseignantDrawer } from "../../context/EnseignantDrawerContext";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── HELPERS ──────────────────────────────────────────────────────────
const formatHeure = (heure) => {
  if (!heure) return '-';
  return heure.split('.')[0];
};

const getPeriodeIcon = (periode) => {
  switch (periode) {
    case 'matin': return <i className="ti ti-sunrise" />;
    case 'apres_midi': return <i className="ti ti-sunset" />;
    case 'descente': return <i className="ti ti-arrow-down-circle" />;
    case 'pause': return <i className="ti ti-coffee" />;
    case 'autre': return <i className="ti ti-alert-triangle" />;
    default: return <i className="ti ti-clock" />;
  }
};

const getPeriodeColor = (periode) => {
  switch (periode) {
    case 'matin': return '#1976d2';
    case 'apres_midi': return '#388e3c';
    case 'descente': return '#d32f2f';
    case 'pause': return '#0288d1';
    case 'autre': return '#f57c00';
    default: return '#6c757d';
  }
};

export default function ENHistoriquePointages({ slug }) {
  const navigate = useNavigate();
  const { getMesClasses, getHistoriquePointages } = useEnseignantService();
  const { openDrawer } = useEnseignantDrawer();

  const [pointages, setPointages] = useState([]);
  const [filteredPointages, setFilteredPointages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState("");
  const [currentClasse, setCurrentClasse] = useState(null);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [periodeFilter, setPeriodeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMotif, setSelectedMotif] = useState({ show: false, motif: "", eleve: "" });

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES CLASSES ─────────────────────────────────────────
  const fetchClasses = useCallback(async () => {
    if (!slug) {
      setError("Identifiant établissement manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getMesClasses(slug);
      setClasses(data || []);
      if (data && data.length > 0) {
        setSelectedClasse(data[0].id);
        setCurrentClasse(data[0]);
      }
    } catch (err) {
      setError("Impossible de charger vos classes.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchClasses();
  }, [fetchClasses]);

  // ─── CHARGEMENT DES POINTAGES ───────────────────────────────────────
  const fetchPointages = useCallback(async () => {
    if (!slug || !selectedClasse) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getHistoriquePointages(slug, {
        classe: selectedClasse,
        date: dateFilter,
        page_size: 'all'
      });
      const data = res.results || res || [];
      setPointages(data);
      setFilteredPointages(data);
    } catch (err) {
      setError("Impossible de charger les pointages.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, selectedClasse, dateFilter]);

  useEffect(() => {
    if (selectedClasse) {
      fetchPointages();
    }
  }, [selectedClasse, dateFilter, fetchPointages]);

  // ─── FILTRES LOCAUX ─────────────────────────────────────────────────
  useEffect(() => {
    let filtered = pointages;

    if (periodeFilter) {
      filtered = filtered.filter(p => p.periode === periodeFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter(p => p.type_pointage === typeFilter);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        (p.eleve_nom || '').toLowerCase().includes(searchLower) ||
        (p.eleve_prenom || '').toLowerCase().includes(searchLower)
      );
    }

    setFilteredPointages(filtered);
  }, [pointages, periodeFilter, typeFilter, searchTerm]);

  // ─── HELPERS ────────────────────────────────────────────────────────
  const handleClasseChange = (classeId) => {
    const classe = classes.find(c => c.id === classeId);
    setSelectedClasse(classeId);
    setCurrentClasse(classe);
    setSearchTerm("");
    setPeriodeFilter("");
    setTypeFilter("");
  };

  const openMotifModal = (pointage) => {
    setSelectedMotif({
      show: true,
      motif: pointage.motif_sortie || "Aucun motif spécifié",
      eleve: `${pointage.eleve_prenom || ''} ${pointage.eleve_nom || ''}`
    });
  };

  const closeMotifModal = () => {
    setSelectedMotif({ show: false, motif: "", eleve: "" });
  };

  const handleExportPDF = async () => {
    try {
      if (filteredPointages.length === 0) {
        await Swal.fire({
          title: 'Aucune donnée',
          text: 'Aucun pointage à exporter pour les critères sélectionnés.',
          icon: 'warning',
          confirmButtonColor: '#1B4F80'
        });
        return;
      }

      const doc = new jsPDF();
      const className = currentClasse?.nom || 'Classe';

      doc.setFontSize(16);
      doc.text(`Pointages - ${className}`, 14, 20);
      doc.setFontSize(12);
      doc.text(`Date: ${dateFilter} | Total: ${filteredPointages.length} pointage(s)`, 14, 30);

      const tableColumn = ["Nom", "Prénom", "Heure", "Type", "Période", "Statut", "Motif"];
      const tableRows = [];

      filteredPointages.forEach((p) => {
        tableRows.push([
          p.eleve_nom || '-',
          p.eleve_prenom || '-',
          formatHeure(p.heure),
          p.type_pointage === "entree" ? "Entrée" : "Sortie",
          p.periode === "autre" ? "Exceptionnelle" :
            p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace('_', ' ') || '-',
          p.statut || '-',
          p.motif_sortie || '-'
        ]);
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [27, 79, 128] }
      });

      doc.save(`pointages_${className}_${dateFilter}.pdf`);

      await Swal.fire({
        title: 'PDF généré',
        text: 'Le fichier PDF a été généré avec succès !',
        icon: 'success',
        confirmButtonColor: '#1B4F80'
      });

    } catch (error) {
      await Swal.fire({
        title: 'Erreur',
        text: 'Erreur lors de la génération du PDF',
        icon: 'error',
        confirmButtonColor: '#d9534f'
      });
    }
  };

  // ─── HEADER ────────────────────────────────────────────────────────
  const Header = () => (
    <div
      style={{
        width: "100%",
        padding: "52px 16px 40px",
        background: "linear-gradient(135deg, var(--pr-dk), var(--pr))",
        borderRadius: "0 0 40px 40px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.22)",
        position: "relative",
        textAlign: "center",
        overflow: "hidden",
      }}
    >
      <div style={{
        position: "absolute",
        left: "20%",
        right: "20%",
        bottom: 0,
        height: 3,
        background: "linear-gradient(90deg, transparent, var(--or), transparent)",
      }} />

      <button
        onClick={openDrawer}
        aria-label="Ouvrir le menu"
        style={{
          position: "absolute",
          top: 44,
          left: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 12,
          width: 42,
          height: 42,
          cursor: "pointer",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 22 }} />
      </button>

      <button
        onClick={() => navigate(`/${slug}/enseignant/accueil`)}
        aria-label="Retour à l'accueil"
        style={{
          position: "absolute",
          top: 44,
          right: 16,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: 12,
          width: 42,
          height: 42,
          cursor: "pointer",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <i className="ti ti-home" style={{ color: "#fff", fontSize: 20 }} />
      </button>

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 10,
          }}
        >
          <i className="ti ti-clock" style={{ fontSize: 30, color: "#fff" }} />
        </div>
        <h1
          style={{
            fontFamily: "Poppins, sans-serif",
            color: "#fff",
            fontSize: 22,
            fontWeight: "bold",
            textShadow: "0 2px 6px rgba(0,0,0,0.3)",
          }}
        >
          Voir les Pointages
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            color: "rgba(255,255,255,0.75)",
            fontSize: 13,
            marginTop: 6,
          }}
        >
          Consultez et gérez l'historique des pointages de vos élèves
        </p>
      </div>
    </div>
  );

  // ─── RENDU ─────────────────────────────────────────────────────────
  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }

          /* ─── Carte d'erreur intégrée ─── */
          .en-histo-error {
            background: #fff;
            border-radius: 20px;
            padding: 40px 20px;
            text-align: center;
            box-shadow: 0 4px 18px rgba(0,0,0,0.08);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            margin: 16px;
          }
          .en-histo-error i.alert-icon {
            font-size: 48px;
            color: var(--rd);
          }
          .en-histo-error h2 {
            font-family: 'Poppins', sans-serif;
            font-size: 17px;
            font-weight: 600;
            color: var(--t);
            margin: 4px 0 0;
          }
          .en-histo-error p {
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            color: var(--m);
            margin: 0 0 12px;
          }
          .en-histo-error .actions-row {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
          }
          .en-histo-error .btn {
            border: none;
            border-radius: 30px;
            padding: 10px 22px;
            cursor: pointer;
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            font-weight: 600;
            color: #fff;
            background: var(--pr);
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: opacity 0.2s;
          }
          .en-histo-error .btn:hover { opacity: 0.9; }
          .en-histo-error .btn.btn-secondary {
            background: var(--b2);
            color: var(--t);
          }
        `}
      </style>

      <div
        style={{
          background: "var(--s2)",
          minHeight: "100vh",
        }}
      >
        <Header />

        {error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="en-histo-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button
                className="btn"
                onClick={() => {
                  hasFetchedRef.current = false;
                  if (!classes.length) {
                    fetchClasses();
                  } else {
                    fetchPointages();
                  }
                }}
              >
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/enseignant/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <div style={{ padding: 16, paddingBottom: 40 }}>
            {/* Sélecteur de classe */}
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 20,
                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                marginBottom: 20,
                animation: "fadeUp 0.4s ease both",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
                <i className="ti ti-school" style={{ color: "var(--pr)", fontSize: 20 }} />
                <h3 style={{ margin: 0, color: "var(--pr)", fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: "bold" }}>
                  Sélectionnez une classe
                </h3>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  overflowX: "auto",
                  gap: 10,
                  paddingBottom: 8,
                  scrollbarWidth: "thin",
                }}
              >
                {classes.map(classe => (
                  <button
                    key={classe.id}
                    onClick={() => handleClasseChange(classe.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 16px",
                      borderRadius: 30,
                      cursor: "pointer",
                      border: `1.5px solid ${selectedClasse === classe.id ? "var(--pr)" : "#e5e7eb"}`,
                      background: selectedClasse === classe.id ? "var(--pr)" : "#fff",
                      color: selectedClasse === classe.id ? "#fff" : "var(--pr)",
                      fontWeight: 600,
                      fontSize: 13,
                      fontFamily: "Inter, sans-serif",
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <i className="ti ti-school" style={{ fontSize: 14 }} />
                    <span>{classe.nom}</span>
                  </button>
                ))}
              </div>
            </div>

            {currentClasse && (
              <>
                {/* Filtres */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: 20,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    marginBottom: 20,
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.05s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
                    <i className="ti ti-filter" style={{ color: "var(--pr)" }} />
                    <h4 style={{ margin: 0, color: "var(--pr)", fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: "bold" }}>
                      Filtres
                    </h4>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f9fafb", padding: "10px 12px", borderRadius: 12 }}>
                      <i className="ti ti-calendar" style={{ color: "var(--pr)" }} />
                      <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 14,
                        }}
                      />
                    </div>

                    <select
                      value={periodeFilter}
                      onChange={(e) => setPeriodeFilter(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        background: "#f9fafb",
                      }}
                    >
                      <option value="">Toutes périodes</option>
                      <option value="matin">Matin</option>
                      <option value="apres_midi">Après-midi</option>
                      <option value="pause">Pause</option>
                      <option value="descente">Descente</option>
                      <option value="autre">Exceptionnelle</option>
                    </select>

                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        background: "#f9fafb",
                      }}
                    >
                      <option value="">Tous types</option>
                      <option value="entree">Entrées</option>
                      <option value="sortie">Sorties</option>
                    </select>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f9fafb", padding: "10px 12px", borderRadius: 12 }}>
                      <i className="ti ti-info-circle" style={{ color: "var(--pr)" }} />
                      <input
                        type="text"
                        placeholder="Rechercher un élève..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          flex: 1,
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 14,
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{ background: "none", border: "none", cursor: "pointer" }}
                        >
                          <i className="ti ti-x" style={{ fontSize: 14, color: "#9ca3af" }} />
                        </button>
                      )}
                    </div>

                    <button
                      onClick={handleExportPDF}
                      disabled={filteredPointages.length === 0}
                      style={{
                        width: "100%",
                        padding: "12px",
                        background: filteredPointages.length === 0 ? "#9ca3af" : "#28a745",
                        border: "none",
                        borderRadius: 30,
                        color: "#fff",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: "bold",
                        fontSize: 14,
                        cursor: filteredPointages.length === 0 ? "default" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        marginTop: 8,
                      }}
                    >
                      <i className="ti ti-download" />
                      Exporter PDF
                    </button>

                    {(periodeFilter || typeFilter || searchTerm) && (
                      <button
                        onClick={() => {
                          setPeriodeFilter("");
                          setTypeFilter("");
                          setSearchTerm("");
                        }}
                        style={{
                          width: "100%",
                          padding: "10px",
                          background: "transparent",
                          border: `1px solid #dc3545`,
                          borderRadius: 30,
                          color: "#dc3545",
                          fontFamily: "Inter, sans-serif",
                          fontSize: 13,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          marginTop: 4,
                        }}
                      >
                        <i className="ti ti-x" style={{ fontSize: 12 }} />
                        Effacer les filtres
                      </button>
                    )}
                  </div>
                </div>

                {/* Résultats */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: 20,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                    animation: "fadeUp 0.4s ease both",
                    animationDelay: "0.1s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                      paddingBottom: 12,
                      borderBottom: "2px solid #f0f0f0",
                    }}
                  >
                    <h4 style={{ margin: 0, color: "var(--pr)", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: "bold" }}>
                      📊 {filteredPointages.length} résultat(s)
                    </h4>
                  </div>

                  {loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                      <div className="loader-spinner" style={{ margin: "0 auto" }} />
                      <p style={{ marginTop: 12, color: "#6b7280", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                        Chargement des pointages...
                      </p>
                    </div>
                  ) : filteredPointages.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                      <i className="ti ti-calendar" style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }} />
                      <p style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                        Aucun pointage trouvé
                      </p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: 500, overflowY: "auto" }}>
                      {filteredPointages.map((pointage, index) => (
                        <div
                          key={pointage.id}
                          style={{
                            padding: "12px 0",
                            borderBottom: index < filteredPointages.length - 1 ? "1px solid #f0f0f0" : "none",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                            <div>
                              <span style={{ fontWeight: "bold", fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                                {pointage.eleve_prenom || ''} {pointage.eleve_nom || ''}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                                  <i className="ti ti-clock" style={{ fontSize: 10 }} /> {formatHeure(pointage.heure)}
                                </span>
                                <span style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 12,
                                  fontWeight: "bold",
                                  color: pointage.type_pointage === 'entree' ? '#28a745' : '#dc3545',
                                }}>
                                  {pointage.type_pointage === 'entree' ? <i className="ti ti-login" style={{ fontSize: 10 }} /> : <i className="ti ti-logout" style={{ fontSize: 10 }} />}
                                  {pointage.type_pointage === 'entree' ? 'Entrée' : 'Sortie'}
                                </span>
                                <span style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 12,
                                  color: getPeriodeColor(pointage.periode),
                                }}>
                                  {getPeriodeIcon(pointage.periode)}
                                  {pointage.periode === "autre" ? "Exceptionnelle" :
                                   pointage.periode?.charAt(0).toUpperCase() + pointage.periode?.slice(1).replace('_', ' ') || '-'}
                                </span>
                              </div>
                            </div>
                            <div>
                              {pointage.motif_sortie ? (
                                <button
                                  onClick={() => openMotifModal(pointage)}
                                  style={{
                                    background: "transparent",
                                    border: "1px solid var(--pr)",
                                    borderRadius: 20,
                                    padding: "4px 10px",
                                    cursor: "pointer",
                                    fontSize: 11,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    color: "var(--pr)",
                                  }}
                                >
                                  <i className="ti ti-info-circle" style={{ fontSize: 10 }} />
                                  Motif
                                </button>
                              ) : (
                                <span style={{ fontSize: 11, color: "#9ca3af" }}>-</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 12,
                              fontSize: 10,
                              fontWeight: 600,
                              background: pointage.statut === 'PRESENT' ? '#d4edda' :
                                        pointage.statut === 'RETARD' ? '#fff3cd' : '#f8d7da',
                              color: pointage.statut === 'PRESENT' ? '#155724' :
                                     pointage.statut === 'RETARD' ? '#856404' : '#721c24',
                            }}>
                              {pointage.statut || '-'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal pour afficher le motif */}
      {selectedMotif.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1050,
          }}
          onClick={closeMotifModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 20,
              padding: 24,
              maxWidth: 340,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeMotifModal}
              style={{
                position: 'absolute',
                top: 15,
                right: 15,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="ti ti-x" style={{ fontSize: 16, color: "#9ca3af" }} />
            </button>
            <h5 style={{
              color: "var(--pr)",
              marginBottom: 16,
              fontFamily: "Poppins, sans-serif",
              fontSize: 16,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              <i className="ti ti-info-circle" style={{ fontSize: 16 }} />
              Motif de sortie - {selectedMotif.eleve}
            </h5>
            <div style={{
              fontSize: 14,
              textAlign: 'justify',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              lineHeight: 1.5,
              backgroundColor: '#f8f9fa',
              padding: 12,
              borderRadius: 12,
              border: '1px solid #e9ecef',
              fontFamily: "Inter, sans-serif",
            }}>
              {selectedMotif.motif}
            </div>
          </div>
        </div>
      )}
    </>
  );
}